import { useMemo } from 'react';
import { FormatterNode } from '../../../../types/FormatterNode';
import type { StrategyFamily } from '../../../../types/Strategy';
import { DynamicField } from '../../common/DynamicField';
import { groupByCategory } from '../../../../utils/groupBy';
import { useFormatterTypes, type FormatterTypeSpec } from '../../../../hooks/useFormatterTypes';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import "./ComponentCard.css";

interface Props {
  node: FormatterNode;
  strategyFamily?: StrategyFamily;
}

const ALPHABOOK_SPREAD_CAP_DEFAULTS: Record<string, number> = {
  'TTF|DA|EEX_SPOT': 1.0,
  'TTF|FM|ICE': 0.3,
  'THE|DA|EEX_SPOT': 1.0,
  'PEG|DA|EEX_SPOT': 1.5,
  'NBP|DA|EEX_SPOT': 4.0,
  'NBP|FM|ICE': 1.0,
};

function normalizeFormatterCardConfig(config: any): { typeId: string; props: Record<string, any>; child: any } {
  if (!config || typeof config !== 'object') {
    return { typeId: '', props: {}, child: null };
  }

  const child = config.child ?? null;
  let cursor = { ...config };
  delete cursor.child;
  let typeId = cursor.typeId ?? cursor.type_id ?? '';
  while (cursor.props && typeof cursor.props === 'object' && !Array.isArray(cursor.props)) {
    const nested = cursor.props;
    if (!typeId && (nested.typeId ?? nested.type_id)) {
      typeId = nested.typeId ?? nested.type_id;
    }
    cursor = nested;
  }

  const props = Object.fromEntries(
    Object.entries(cursor).filter(([key]) => !['typeId', 'type_id', 'child', 'start_date', 'end_date'].includes(key))
  );

  return { typeId, props, child };
}

function resolveInheritedSpreadCap(node: FormatterNode): number | undefined {
  const normalized = normalizeFormatterCardConfig(node.config);
  if (normalized.typeId !== 'meepmeep_lagged_return_ema') return undefined;
  const child = normalized.child;
  if (!child || child.type !== 'Single') return undefined;

  const childProps = child.config?.props ?? child.config ?? {};
  const market = String(childProps.market ?? '').trim().toUpperCase();
  const product = String(childProps.product ?? '').trim().toUpperCase();
  const broker = String(childProps.broker ?? '').trim().toUpperCase();
  return ALPHABOOK_SPREAD_CAP_DEFAULTS[`${market}|${product}|${broker}`];
}

export function FormatterComponentCard({ node, strategyFamily = 'base' }: Props) {
  const { onUpdate } = useNodeOperations();
  const { formatterTypes, getFormatterTypeById, getOptions } = useFormatterTypes();
  const { typeId, props, child } = normalizeFormatterCardConfig(node.config);
  const selectedType = useMemo(() => getFormatterTypeById(typeId), [getFormatterTypeById, typeId]);
  const filteredFormatterTypes = useMemo(
    () => formatterTypes.filter((formatterType) => (formatterType.strategy_grammars ?? ['base', 'quantarc']).includes(strategyFamily)),
    [formatterTypes, strategyFamily],
  );

  const resolvedProps = useMemo(() => {
    if (!selectedType) return props;

    const nextProps: Record<string, any> = { ...props };
    const inheritedSpreadCap = resolveInheritedSpreadCap(node);
    for (const property of selectedType.properties) {
      if (
        property.key === 'spread_cap' &&
        inheritedSpreadCap !== undefined &&
        (nextProps[property.key] === undefined || nextProps[property.key] === null || nextProps[property.key] === '')
      ) {
        nextProps[property.key] = inheritedSpreadCap;
        continue;
      }
      if (nextProps[property.key] === undefined || nextProps[property.key] === null || nextProps[property.key] === '') {
        if (property.default !== undefined) {
          nextProps[property.key] = property.default;
        }
      }
    }
    return nextProps;
  }, [node, props, selectedType]);

  const handleTypeChange = (newTypeId: string) => {
    const updated = new FormatterNode(node.id, node.title, node.isManualRename);
    const newTypeSpec = getFormatterTypeById(newTypeId);

    const newProps: Record<string, any> = {};
    if (newTypeSpec) {
      for (const p of newTypeSpec.properties) {
        newProps[p.key] = p.default ?? null;
      }
    }

    updated.config = { typeId: newTypeId, props: newProps, child: node.config.child };
    onUpdate(node.id, updated);
  };

  const handlePropChange = (key: string, value: any) => {
    const updated = new FormatterNode(node.id, node.title, node.isManualRename);
    updated.config = { typeId, props: { ...props, [key]: value }, child };
    onUpdate(node.id, updated);
  };

  return (
    <div className="internal-card-container">

      {/* ── Type selector ─────────────────────────────────────── */}
      <div className="field-row">
        <label className="field-label">Transform</label>
        <div className="flex-1 flex items-center gap-xs">
          <div className="custom-select-container flex-1">
            <select
              value={typeId}
              onChange={(e) => handleTypeChange(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              className="native-select"
            >
              <option value="">-- Select Type --</option>
              {Object.entries(groupByCategory<FormatterTypeSpec>(filteredFormatterTypes)).map(([category, items]) => (
                <optgroup key={category} label={category}>
                  {items.map((dt) => (
                    <option key={dt.id} value={dt.id}>{dt.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <div className="select-label-overlay">
              <span>{filteredFormatterTypes.find(dt => dt.id === typeId)?.label || selectedType?.label || "-- Select Type --"}</span>
              <span className="select-arrow">▼</span>
            </div>
          </div>

          {selectedType?.description && (
            <span title={selectedType.description}
              style={{ cursor: 'help', opacity: 0.5, fontSize: '1rem' }}>
              🛈
            </span>
          )}
        </div>
      </div>

      {/* ── Dynamic props ─────────────────────────────────────── */}
      {!!typeId && selectedType && (
        <div className="flex flex-col gap-sm">
          {selectedType.properties.map((p: any) => (
            <DynamicField
              key={p.key}
              context={resolvedProps}
              spec={p}
              value={resolvedProps?.[p.key]}
              onChange={(v) => handlePropChange(p.key, v)}
              getOptions={getOptions}
            />
          ))}
        </div>
      )}

    </div>
  );
}
